// React imports
import React, { useEffect, useState } from "react";

// External library imports
import {
	Box,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Alert,
	CircularProgress,
	TextField,
	InputAdornment,
	Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import { useSnackbar } from "notistack";
import * as XLSX from "xlsx";

// Local imports
import { employeesWithNoSkillResponses } from "../../Service/Apis";
import { Team, Employee, ApiResponse } from "./Types/SkillTypes";
import { StandardTablePagination } from "components/Pagination/StandardTablePagination";
import { PAGINATION_CONFIG } from "utils/paginationConfig";
import PageTitle from "../../components/Page Component/PageTitle";

const EmployeesWithNoSkillResponses: React.FC = () => {
	// useState hooks
	const [employees, setEmployees] = useState<Employee[]>([]);
	const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [page, setPage] = useState<number>(0);
	const [rowsPerPage, setRowsPerPage] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);

	// Other constants
	const { enqueueSnackbar } = useSnackbar();

	// Functions
	const fetchEmployees = async () => {
		setLoading(true);
		try {
			const token = localStorage.getItem("token");
			const response = await axios.get<ApiResponse>(
				employeesWithNoSkillResponses,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (response.data && response.data.employees) {
				setEmployees(response.data.employees);
				setFilteredEmployees(response.data.employees);
			}
		} catch (error: any) {
			console.error("Error fetching employees:", error);
			enqueueSnackbar(
				error?.response?.data?.message ||
					"Failed to fetch employees. Please try again.",
				{ variant: "error" }
			);
		} finally {
			setLoading(false);
		}
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleDownloadReport = () => {
		if (filteredEmployees.length === 0) {
			enqueueSnackbar("No data to download", { variant: "warning" });
			return;
		}

		// Prepare data for Excel
		const excelData = filteredEmployees.map((employee, index) => ({
			"S.No": index + 1,
			"Employee Code": employee.employee_code,
			"First Name": employee.first_name,
			"Last Name": employee.last_name,
			"Email": employee.email,
		}));

		// Create worksheet
		const worksheet = XLSX.utils.json_to_sheet(excelData);

		// Set column widths
		const columnWidths = [
			{ wch: 8 },  // S.No
			{ wch: 15 }, // Employee Code
			{ wch: 20 }, // First Name
			{ wch: 20 }, // Last Name
			{ wch: 30 }, // Email
		];
		worksheet["!cols"] = columnWidths;

		// Create workbook
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Employees Without Skills");

		// Generate filename with current date
		const currentDate = new Date().toISOString().split("T")[0];
		const filename = `Employees_Without_Skill_Assessments_${currentDate}.xlsx`;

		// Download file
		XLSX.writeFile(workbook, filename);

		enqueueSnackbar(
			`Report downloaded successfully! (${filteredEmployees.length} employees)`,
			{ variant: "success" }
		);
	};

	// useEffect hooks (must be before any returns per React Rules of Hooks)
	useEffect(() => {
		fetchEmployees();
	}, []);

	useEffect(() => {
		if (searchTerm.trim() === "") {
			setFilteredEmployees(employees);
			setPage(0);
		} else {
			const filtered = employees.filter((employee) => {
				const searchLower = searchTerm.toLowerCase();
				return (
					employee.first_name.toLowerCase().includes(searchLower) ||
					employee.last_name.toLowerCase().includes(searchLower) ||
					employee.email.toLowerCase().includes(searchLower) ||
					employee.employee_code.toLowerCase().includes(searchLower)
				);
			});
			setFilteredEmployees(filtered);
			setPage(0);
		}
	}, [searchTerm, employees]);

	// Main JSX return with ternary operator for loading state
	return loading ? (
		<Box
			display="flex"
			justifyContent="center"
			alignItems="center"
			minHeight="400px"
		>
			<CircularProgress />
		</Box>
	) : (
		<Box sx={{ width: "100%", mt: 2, mb: 10, p: 3 }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 3,
				}}
			>
			<Box>
				<PageTitle>Employees Without Skill Assessments</PageTitle>
				<Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
					Active employees who haven't completed any skill assessments yet
				</Typography>
			</Box>
				<Button
					variant="contained"
					color="primary"
					startIcon={<DownloadIcon />}
					onClick={handleDownloadReport}
					disabled={loading || employees.length === 0}
					sx={{ minWidth: 150, textTransform: "none" }}
				>
					Download Report
				</Button>
			</Box>

			{employees.length === 0 ? (
				<Alert severity="success" icon={<PersonOffIcon />}>
					<Typography variant="h6">
						Excellent! All active employees have completed their skill
						assessments.
					</Typography>
				</Alert>
			) : (
				<>
					{/* Search Box */}
					<TextField
						fullWidth
						variant="outlined"
						placeholder="Search by name, email, or employee code..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						sx={{ mb: 3 }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
					/>

					{/* Results Count */}
					<Typography variant="body2" color="black" sx={{ mb: 2 }}>
						Showing {filteredEmployees.length} of {employees.length} employees
					</Typography>

					{/* Table */}
					<TableContainer component={Paper} elevation={3}>
						<Table sx={{ minWidth: 650 }} aria-label="employees table">
							<TableHead>
								<TableRow sx={{ backgroundColor: "#f5f5f5" }}>
									<TableCell>
										<strong>Employee Code</strong>
									</TableCell>
									<TableCell>
										<strong>Name</strong>
									</TableCell>
									<TableCell>
										<strong>Email</strong>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredEmployees.length === 0 ? (
									<TableRow>
										<TableCell colSpan={3} align="center">
											<Typography variant="body1" color="black">
												No employees found matching your search criteria
											</Typography>
										</TableCell>
									</TableRow>
								) : (
									filteredEmployees
										.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
										.map((employee) => (
											<TableRow
												key={employee.id}
												hover
												sx={{
													"&:hover": {
														backgroundColor: "#fafafa",
													},
												}}
											>
												<TableCell>{employee.employee_code}</TableCell>
												<TableCell>
													{employee.first_name} {employee.last_name}
												</TableCell>
												<TableCell>{employee.email}</TableCell>
											</TableRow>
										))
								)}
							</TableBody>
						</Table>
					</TableContainer>

					{/* Pagination */}
					<StandardTablePagination
						component="div"
						count={filteredEmployees.length}
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</>
			)}
		</Box>
	);
};

export default EmployeesWithNoSkillResponses;

